import { Fragment, useState } from "react"
import { capitalize, maxBy, startCase } from "lodash"
import { groupBy } from "~/utils/tsUtils"

import { api } from "~/utils/api"
import {
  DateFormatType,
  formatDate,
  formatRelativeDate,
  isSameDay,
  startOfTodayUTC,
} from "~/utils/datetime"
import { formatDuration, formatNumberWithSuffix, getDurationColor } from "~/utils/formatting"

import { Badge } from "../reusable/Badge"
import { Button } from "../reusable/Button"
import { Card, CardHeader, CardTitle } from "../reusable/Card"
import SuperChart, { type DateChartSeries } from "../reusable/graphs/SuperChart"
import { Skeleton } from "../reusable/Skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../reusable/Table"
import { Tooltip } from "../reusable/Tooltip"
import { ChartType } from "~/constants/graphs"
import { useCoreIngestionLogs } from "~/hooks/useCoreIngestion"
import { RowDelta } from "~/components/status/RowDelta"
import { isFailedTableUpdate, rowDeltaByRun, tableUpdateStatus } from "~/utils/tableUpdates"

interface OrgTableMetaDataCardProps {
  orgId: string
  shouldRefresh?: boolean
}

export const OrgTableMetaDataCard: React.FC<OrgTableMetaDataCardProps> = ({
  orgId,
  shouldRefresh = false,
}) => {
  const { data: updateMeta } = api.updatedAt.getOrgTableMetaLogs.useQuery({ orgId })
  const fullUpdateMeta = useCoreIngestionLogs(orgId, shouldRefresh)
  const latestUpdated = maxBy(updateMeta, (meta) => meta.endTime)?.endTime
  const updateMetaGroupedByTable = groupBy(fullUpdateMeta, "tableName")

  const [showTableNames, setShowTableNames] = useState<string[]>([])
  const [sortBySlowest, setSortBySlowest] = useState(false)

  const rowDeltaByRecord = rowDeltaByRun(
    fullUpdateMeta,
    (r) => r.tableName,
    (r) => `${r.tableName}:${r.startTime.getTime()}`,
  )

  if (!updateMeta) {
    return <Skeleton className="h-[627px]" />
  }

  const generateSeries = (tableName: string): DateChartSeries => {
    if (!updateMetaGroupedByTable[tableName]) {
      return {
        name: startCase(tableName),
        data: [],
        colors: ["rgb(147, 231, 110)"],
        type: "areaspline",
      }
    }

    return {
      name: startCase(tableName),
      data: updateMetaGroupedByTable[tableName]!.map((meta) => ({
        milliseconds: meta.startTime.getTime(),
        value: meta.nRows,
      })),
      colors: ["rgb(147, 231, 110)"],
      type: "areaspline",
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row gap-2 items-center justify-start">
        <CardTitle>Last data ingestion per table</CardTitle>
        {shouldRefresh && (
          <Tooltip className="capitalize" title="Real-time ingestion status">
            <div className="animate-ping w-2 h-2 rounded-full bg-violet-500" />
          </Tooltip>
        )}
        <Button
          size="sm"
          variant={sortBySlowest ? "primary" : "neutral"}
          onClick={() => setSortBySlowest(!sortBySlowest)}
        >
          Sort by slowest
        </Button>
      </CardHeader>

      <Table>
        <TableHeader>
          <TableHead>Table name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead># of rows</TableHead>
          <TableHead>Row Δ</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead className="text-right">Last updated at</TableHead>
          <TableHead></TableHead>
        </TableHeader>
        <TableBody>
          {[...updateMeta]
            .sort((a, b) => {
              if (!sortBySlowest) return 0
              const durationA =
                a.startTime && a.endTime ? a.endTime.getTime() - a.startTime.getTime() : 0
              const durationB =
                b.startTime && b.endTime ? b.endTime.getTime() - b.startTime.getTime() : 0
              return durationB - durationA
            })
            .map((meta) => {
              const hasFailed = isFailedTableUpdate(meta)
              const updateStatus = tableUpdateStatus(meta)
              const delta = rowDeltaByRecord.get(`${meta.tableName}:${meta.startTime.getTime()}`)

              return (
                <Fragment key={meta.tableName}>
                  <TableRow>
                    <TableCell className="font-medium">{meta.tableName}</TableCell>
                    <TableCell>
                      <Tooltip title={updateStatus.tooltip}>
                        <Badge size="sm" variant={updateStatus.variant}>
                          {updateStatus.label}
                        </Badge>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={meta.isCached ? updateStatus.tooltip : ""}>
                        {hasFailed ? (
                          <span className="text-secondary">—</span>
                        ) : (
                          <span>{formatNumberWithSuffix(meta.nRows, 2)}</span>
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <RowDelta delta={meta.isCached || hasFailed ? null : (delta ?? null)} />
                    </TableCell>
                    <TableCell className={getDurationColor(meta.startTime, meta.endTime)}>
                      {meta.endTime ? formatDuration(meta.startTime, meta.endTime) : "—"}
                    </TableCell>
                    <TableCell
                      className={`text-right ${!meta.endTime || (meta.endTime && latestUpdated && !isSameDay(meta.endTime, latestUpdated)) ? "text-red-500" : ""}`}
                    >
                      <Tooltip
                        className="text-center w-[300px]"
                        title={
                          meta.endTime && latestUpdated && !isSameDay(meta.endTime, latestUpdated)
                            ? `${meta.tableName} was last updated on ${formatDate(meta.endTime)} which is not the same day as the latest update on ${formatDate(latestUpdated)}`
                            : ""
                        }
                      >
                        <div>
                          {meta.endTime
                            ? capitalize(
                                formatRelativeDate(
                                  meta.endTime,
                                  startOfTodayUTC(),
                                  DateFormatType.WithTime,
                                ),
                              )
                            : "N/A"}
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="neutral"
                        onClick={() =>
                          setShowTableNames(
                            showTableNames.includes(meta.tableName)
                              ? showTableNames.filter((name: string) => name !== meta.tableName)
                              : [...showTableNames, meta.tableName],
                          )
                        }
                      >
                        {showTableNames.includes(meta.tableName) ? "Hide" : "Show"} history
                      </Button>
                    </TableCell>
                  </TableRow>
                  {showTableNames.includes(meta.tableName) && (
                    <TableRow key={`${meta.tableName}-full`}>
                      <TableCell colSpan={7} className="font-medium">
                        <SuperChart
                          type={ChartType.TimeSeries}
                          height={314}
                          data={[generateSeries(meta.tableName)]}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
        </TableBody>
      </Table>
    </Card>
  )
}
